// Notice form (DSA art. 16) and terms of use. Both work without logging in: the infosite links
// here from its footer. CONCEPT texts — have them checked before a public launch
// (docs/JURIDISCH.md).
import { useState } from "react";
import { publicApi, type NoticeInput } from "../api";
import { useAuth } from "../auth";
import { Card, ErrorNote } from "../ui";

const EMPTY: NoticeInput = {
  kind: "copyright",
  reporter_name: "",
  reporter_email: "",
  reporter_role: "rights_holder",
  organisation: "",
  work_title: "",
  work_author: "",
  isbn: "",
  location: "",
  explanation: "",
  good_faith: false,
  accurate: false,
};

export function NoticePage() {
  const { config } = useAuth();
  const [form, setForm] = useState<NoticeInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ id: string; created_at: string } | null>(null);
  const set = <K extends keyof NoticeInput>(key: K, value: NoticeInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const clean = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? undefined : v]),
      ) as NoticeInput;
      setSent(await publicApi.notice((config?.api_url ?? "").replace(/\/$/, ""), clean));
      setForm(EMPTY);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="prose stack">
      <h1>Inbreuk of misbruik melden</h1>
      <p>
        Zie je op FabulaWiki inhoud die inbreuk maakt op jouw auteursrecht, of die op een andere
        manier niet hoort (privacy, onjuiste informatie over een echt persoon)? Meld het hier. Je
        hebt geen account nodig. We bevestigen de ontvangst met een referentie, beoordelen elke
        melding zorgvuldig en streven naar een besluit binnen vijf werkdagen.
      </p>
      <p className="muted small">
        Wiki's op FabulaWiki zijn standaard privé: alleen de uploader ziet ze. Ook zonder exacte
        locatie kunnen we een melding behandelen: noem titel, auteur en zo mogelijk het ISBN, dan
        zoeken wij de geüploade exemplaren op.
      </p>
      {sent ? (
        <Card>
          <h2>Melding ontvangen</h2>
          <p>
            Dank je. Je referentie is <code>{sent.id}</code> (ontvangen{" "}
            {new Date(sent.created_at).toLocaleString("nl-NL")}). Bewaar deze referentie voor
            eventuele vervolgvragen of bezwaar.
          </p>
          <button className="btn btn-ghost" onClick={() => setSent(null)}>
            Nog een melding doen
          </button>
        </Card>
      ) : (
        <form className="card stack notice-form" onSubmit={(e) => void submit(e)}>
          <label>
            Soort melding
            <select value={form.kind} onChange={(e) => set("kind", e.target.value as NoticeInput["kind"])}>
              <option value="copyright">Auteursrecht</option>
              <option value="privacy">Privacy / persoonsgegevens</option>
              <option value="other">Overig (ook: bezwaar tegen een verwijdering)</option>
            </select>
          </label>
          <label>
            Ik meld als
            <select
              value={form.reporter_role}
              onChange={(e) => set("reporter_role", e.target.value as NoticeInput["reporter_role"])}
            >
              <option value="rights_holder">Rechthebbende (auteur, uitgever)</option>
              <option value="representative">Vertegenwoordiger van de rechthebbende</option>
              <option value="other">Iemand anders</option>
            </select>
          </label>
          <label>
            Naam
            <input required minLength={2} value={form.reporter_name}
                   onChange={(e) => set("reporter_name", e.target.value)} />
          </label>
          <label>
            E-mailadres
            <input required type="email" value={form.reporter_email}
                   onChange={(e) => set("reporter_email", e.target.value)} />
          </label>
          <label>
            Organisatie <span className="muted small">(optioneel)</span>
            <input value={form.organisation} onChange={(e) => set("organisation", e.target.value)} />
          </label>
          <label>
            Titel van het werk
            <input required value={form.work_title} onChange={(e) => set("work_title", e.target.value)} />
          </label>
          <label>
            Auteur <span className="muted small">(optioneel)</span>
            <input value={form.work_author} onChange={(e) => set("work_author", e.target.value)} />
          </label>
          <label>
            ISBN <span className="muted small">(optioneel)</span>
            <input value={form.isbn} onChange={(e) => set("isbn", e.target.value)} />
          </label>
          <label>
            Waar staat het? <span className="muted small">(link of omschrijving, optioneel)</span>
            <input value={form.location} onChange={(e) => set("location", e.target.value)} />
          </label>
          <label>
            Toelichting
            <textarea required minLength={10} rows={5} value={form.explanation}
                      onChange={(e) => set("explanation", e.target.value)}
                      placeholder="Waarom is dit onrechtmatig? Bij bezwaar: je referentie en je argumenten." />
          </label>
          <label className="check">
            <input type="checkbox" checked={form.good_faith}
                   onChange={(e) => set("good_faith", e.target.checked)} />
            <span>Ik doe deze melding te goeder trouw.</span>
          </label>
          <label className="check">
            <input type="checkbox" checked={form.accurate}
                   onChange={(e) => set("accurate", e.target.checked)} />
            <span>De gegevens in deze melding zijn juist en volledig.</span>
          </label>
          <ErrorNote error={error} />
          <div>
            <button className="btn" type="submit" disabled={busy || !form.good_faith || !form.accurate}>
              Melding versturen
            </button>
          </div>
          <p className="muted small">
            We gebruiken je gegevens alleen om de melding te behandelen en je te antwoorden. De
            uploader krijgt een motivering van een eventuele verwijdering, zonder jouw
            contactgegevens.
          </p>
        </form>
      )}
    </article>
  );
}

export function TermsPage() {
  return (
    <article className="prose stack">
      <h1>Gebruiksvoorwaarden</h1>
      <p className="note">
        Concept (versie 2026-10-concept-1) voor de gesloten bèta; nog juridisch te toetsen.
      </p>

      <h2>1. De dienst</h2>
      <p>
        FabulaWiki maakt van een boek dat jij aanlevert een spoilervrij naslagwerk voor jezelf:
        personages, plaatsen, gebeurtenissen en relaties, per hoofdstuk. FabulaWiki is in
        ontwikkeling. Er is geen relatie met auteurs, uitgevers of andere rechthebbenden van de
        boeken; titels en namen worden alleen gebruikt om het boek aan te duiden.
      </p>

      <h2>2. Account</h2>
      <p>
        Nieuwe accounts worden handmatig goedgekeurd. Een beheerder kent gratis boek-credits toe.
        Je account is persoonlijk. Je geeft je land van verblijf naar waarheid op.
      </p>

      <h2>3. Welke boeken je mag uploaden</h2>
      <p>Bij elke upload verklaar je dat:</p>
      <ul>
        <li>
          je het exemplaar rechtmatig hebt verkregen (gekocht, gekregen of uit het publieke
          domein), of dat je de auteur of rechthebbende bent of diens toestemming hebt;
        </li>
        <li>het bestand DRM-vrij is en je geen beveiliging hebt omzeild of verwijderd;</li>
        <li>het bestand niet van een illegale downloadsite komt;</li>
        <li>de wiki voor je eigen, privégebruik is en je hem niet met anderen deelt;</li>
        <li>
          de rechthebbende voor zover jij weet tekst- en datamining (AI-verwerking) niet heeft
          voorbehouden, of dat je daarvoor toestemming hebt;
        </li>
        <li>het om fictie gaat (geen biografie, true crime of teksten over echte personen).</li>
      </ul>
      <p>
        We controleren elk bestand automatisch op DRM, een voorbehoud voor tekst- en datamining en
        kenmerken van illegale bronnen. Dat kan leiden tot een waarschuwing of een weigering; de
        uitkomst wordt bij het boek vastgelegd. Zo'n controle is geen garantie: jouw verklaring
        blijft de basis.
      </p>

      <h2>4. Wat we met je bestand doen</h2>
      <ul>
        <li>
          Je geeft FabulaWiki toestemming het bestand te verwerken, uitsluitend om jouw wiki te
          maken en te tonen.
        </li>
        <li>
          De tekst wordt per hoofdstuk naar een taalmodel gestuurd (standaard DeepSeek; verwerking buiten
          de EU). Je kunt bij het uploaden ook Mistral AI kiezen (experimenteel; verwerking in de EU); andere
          verwerkers zijn nog niet beschikbaar.
        </li>
        <li>
          Het geüploade bestand wordt verwijderd zodra de verwerking klaar is; de hoofdstuktekst
          die tussen de verwerkingsstappen nodig is uiterlijk na 24 uur. Daarna bewaren we alleen
          wat eruit is afgeleid: personages, feiten, korte samenvattingen, en per feit een kort
          citaat dat alleen jij bij het controleren ziet.
        </li>
        <li>
          Wil je je wiki later opnieuw laten opbouwen (bijvoorbeeld na een verbetering van
          FabulaWiki), dan bied je hetzelfde bestand opnieuw aan. Dat kost geen credit.
        </li>
      </ul>

      <h2>5. Privé, en delen</h2>
      <p>
        Je wiki is privé: alleen jij ziet de inhoud, ook beheerders niet. Openbaar maken kan alleen
        voor boeken in het publieke domein of waarvoor een licentie is, na beoordeling door een
        beheerder.
      </p>

      <h2>6. Melden en verwijderen</h2>
      <p>
        Iedereen kan via <a href="#/melden">Inbreuk melden</a> inhoud melden. We beoordelen elke
        melding zorgvuldig. Bij een gegronde melding verwijderen we het boek met alle afgeleide
        gegevens en kunnen we voorkomen dat hetzelfde bestand opnieuw wordt geüpload. Je ziet in
        de app welk boek is verwijderd en waarom; bezwaar maak je via hetzelfde formulier (soort
        "Overig", met de reden die je kreeg). Bij herhaalde inbreuk (in de regel een tweede
        gegronde melding) kunnen we je account blokkeren.
      </p>

      <h2>7. Kwaliteit en aansprakelijkheid</h2>
      <p>
        De wiki wordt automatisch gemaakt en kan fouten bevatten; je kunt die zelf corrigeren.
        Mislukt een verwerking, dan krijg je je credit terug. Voor zover de wet dat toestaat is
        FabulaWiki niet aansprakelijk voor indirecte schade. Uploads in strijd met deze voorwaarden
        zijn voor jouw rekening: je vrijwaart FabulaWiki voor aanspraken van derden die daaruit
        voortvloeien, voor zover de wet dat toestaat.
      </p>

      <h2>8. Einde</h2>
      <p>
        Je kunt je account en al je gegevens op elk moment zelf verwijderen (Mijn boeken → Mijn
        account). Bij misbruik kunnen we je account blokkeren of beëindigen.
      </p>

      <h2>9. Prijs en herroeping</h2>
      <p>
        Tijdens de bèta zijn credits gratis. Voor betaalde credits gelden aanvullende afspraken,
        waaronder over het herroepingsrecht; die volgen voordat er betaald kan worden.
      </p>

      <h2>10. Recht</h2>
      <p>
        Op deze voorwaarden is Nederlands recht van toepassing. Als consument houd je de
        bescherming die het recht van je woonland je geeft.
      </p>
    </article>
  );
}
